﻿/* global jQuery */
import { AsyncUtils } from '../main/ObjectUtils.js';
import { BootstrapStatuses } from './DesignerAppLauncher.js';
import { GlobalErrorHandler } from '../main/GlobalErrorHandler.js';

/** Well-known event id's */
export const EventIds = Object.freeze({
  appDownloadInitiated: 'app-download-initiated',
  appDownloaded: 'app-downloaded',
  appInstalled: 'app-installed',
  appStartInitiated: 'app-start-initiated', // After clicking on start button; takes us to the "Starting" process step
  appStartInitiatedInstallUnknown: 'app-start-initiated-install-unknown',
  gaveUpWaitingForBootstrapLaunch: 'gave-up-waiting-for-bootstrap-launch',
  needHelpGetAppLaunched: 'need-help-get-app-launched',
  bootstrapStatusChanged: 'bootstrapStatusChanged',
  dialogClosed: 'dialog-closed',
  errorOccurred: 'errorOccurred'
});

export class ProcessStep {
  constructor(config) {
    this.config = config;
    this.popupDialog = null; // must be assigned by the owner
    this.dispatcher = null; // must be assigned by the owner
  }

  async doExecute() {
  }

  async execute(initiatingEvent)
  {
    if (this.isExecuting) {
      /* TODO: Consider handling re-entrancy issues here. It might not work, though, if the owner
       * (e.g. StartDesignerProcess) is calling execute without await. */
    }
    this.isExecuting = true;
    try {
      this.initiatingEvent = initiatingEvent;
      this.popupDialog.showBody(this.contentSelector);
      await this.doExecute();
    } catch (ex) {
      this.dispatchException(ex);
    } finally {
      this.isExecuting = false;
    }
  }

  done() {
    this.popupDialog.hideBody();
  }

  dispatchEvent(event) {
    event.triggerStep = this;
    return this.dispatcher.dispatch(event);
  }

  dispatchException(ex) {
    if (ex instanceof Error) {
      console.error(`"${this.id}" step: caught ${ex.name} exception: '${ex.message}'`);
    } else {
      console.error(`"${this.id}" step: caught exception: '${ex}'`);
    }
    this.dispatchEvent({ id: EventIds.errorOccurred, data: ex });
  }

  addDownloadButton() {
    return {
      key: 'download-btn',
      text: this.config.downloadButtonText,
      onAddButtonStyle: {
        // See the comment in jQueryPopupDialog.applyButtonStyles method for more on why we do it this way:
        callback: (index, button, config) => {
          // Inserts the cloud-download svg icon from our common-icons svg sprite file:
          jQuery(button).prepend('<svg class="info-square" style="width:16px;height:16px;fill:none;stroke:currentColor;margin-right:10px;margin-bottom:3px"><use xlink:href="/svg/common-icons.svg#download-cloud"></use></svg>');
          return false; // Unhandled -> let the caller do the remaining styling
        }
      },
      click: () => this.dispatchEvent({ id: EventIds.appDownloadInitiated })
    };
  }
}

//#region --------------- Starting Designer---------------

/** This step is responsible for kick-starting the process when the user clicks on the Start button. */
export class StartButtonStep extends ProcessStep {
  constructor(appLauncher, telemetrySession, config) {
    super(config);
    this.appLauncher = appLauncher;
    this.telemetrySession = telemetrySession;
    const startBtn = jQuery(config.startDesignerBtnSelector);
    startBtn.show();
    startBtn.click(async e => {
      e.preventDefault(); // If the target is an anchor, this prevent the default action (navigating to the href)
      //console.log(`Start button clicked, executing step...`);
      await this.execute();
    });
  }

  /**
   * Executes the StartButtonStep, e.g. the user has initiated the start by clicking on a button.
   * @param {any} initiatingEvent The event which caused this step to be started.
   */
  async doExecute() {
    await super.doExecute();
    this.telemetrySession.reportDesignerButtonClickedAsync(); // We don't need to await this
    this.popupDialog.open();
    /* If Bootstrap has been installed, then let's go for an immediate app start. Otherwise, we
     * may want to ask the user what he wants to do. */
    const eventId = this.appLauncher.isBootstrapInstalled()
      ? EventIds.appStartInitiated
      : EventIds.appStartInitiatedInstallUnknown;
    this.dispatchEvent({ id: eventId });
  }
}

/**
 * Responsible for letting the user choose either Start or Download actions in situations where
 * Designer has not been downloaded and installed before.
 */
export class StartOrDownloadStep extends ProcessStep {
  constructor(appLauncher, config) {
    super(config);
    this.contentSelector = '.step-id-start-or-download'; // The super.doExecute() method uses this property
    this.appLauncher = appLauncher;
    this.buttons = [
      {
        key: 'start-btn',
        text: config.startNowButtonText,
        click: () => this.dispatchEvent({ id: EventIds.appStartInitiated })
      },
      this.addDownloadButton()
    ];
  }

  async doExecute() {
    await super.doExecute();
    this.popupDialog.addButtons(this.buttons);
  }

  done() {
    this.popupDialog.removeButtons(this.buttons);
    super.done();
  }
}

/**
 * This step is responsible for using the custom URL scheme to start the Bootstrap app. The step
 * will remain active until either 1) we receive a status update from the Bootstrap app (in which
 * case we continue the launch), or 2) we time out, in which case we will try to detect the presence
 * of a PHC-dialog blocking the startup. If yes, we'll transition to another step helping the user
 * unblock the startup. If no (no blocking dialog), should give the user more generic help information
 * and allow him to try installing the program instead.
 */
export class StartingStep extends ProcessStep {
  constructor(appLauncher, config) {
    super(config);
    this.contentSelector = '.step-id-starting'; // The super.doExecute() method uses this property
    this.appLauncher = appLauncher;
  }

  // TODO: Test the detection code with older browsers, and implement fallbacks if needed.
  // TODO: Keep monitoring the behavior of focus/blur events in case the browsers change their behavior in the future.

  /**
   * This method uses a focus/blur trick to detect the presence of the so called "Protocol Handler
   * Confirmation-dialog" (PHC-dialog), or "Open this app?" dialog. The trick relies on my finding
   * that the blur event does NOT get raised if the modal PHC-dialog is displayed. As of 2/17/2024
   * this detection code works for the latest versions of Chrome, Edge, and Firefox.
   */
  tryDetectBrowserBlockDialog() {
    this.buttons = [this.addDownloadButton()]; // This button is only needed for detection of the PHC-dialog:
    this.popupDialog.addButtons(this.buttons);
    const $closeBtn = this.popupDialog.findButtonById(this.popupDialog.closeButtonId);
    const $downloadBtn = this.popupDialog.findButtonByKey('download-btn');
    // Add focus event listeners:
    $closeBtn.on('focus', () => console.log('Close Button got focus'));
    $closeBtn.on('blur', () => {
      console.log('Close Button lost focus -> this means we are NOT BLOCKED');
      if (this.dialogDetectionTimeoutId) {
        clearTimeout(this.dialogDetectionTimeoutId);
        this.dialogDetectionTimeoutId = null;
      }
      this.dispatchEvent({ id: EventIds.gaveUpWaitingForBootstrapLaunch })
    });
    $downloadBtn.on('focus', () => console.log('Download Button got focus'));
    $downloadBtn.on('blur', () => console.log('Download Button lost focus'));

    console.log('Setting 3 second timeout...');
    this.dialogDetectionTimeoutId = setTimeout(() => {
      this.dialogDetectionTimeoutId = null;
      console.log('It looks like we are BLOCKED');
      this.dispatchEvent({ id: EventIds.needHelpGetAppLaunched });
    }, 3000);

    /* This is what will set off our detection. If a PHC-dialog has been displayed by the browser,
     * then the blur (lost focus) event on the close button will never be raised. */
    $closeBtn.focus();
    $downloadBtn.focus();
  }

  async doExecute() {
    await super.doExecute();

    this.$launchSpinner = this.popupDialog.findChildElement('.launch-spinner');
    this.$launchSpinner.show();

    if (this.config.preventDesignerFromStarting) { // For testing/debugging the look and feel of the popup
      console.warn('StartingStep: (simulating) preventing Designer from starting...');
      this.popupDialog.displayWarning('NOTE: We are preventing (simulating) Designer from starting!');
      return;
    } else if (this.config.simulateDesignerNotStarting) {
      console.warn('StartingStep: simulating Designer not starting...');
      this.popupDialog.displayWarning('NOTE: We are simulating Designer not starting!');
      this.dispatchEvent({ id: EventIds.gaveUpWaitingForBootstrapLaunch })
      return;
    }

    console.log(`"${this.id}" step: initialing bootstrap launch...`);
    /* Launches the app by eventually making an assignment to window.location. Before it does that,
     * several async calls are awaited. Code below this next statement is not guaranteed to execute. */
    await Promise.all([
      // Starts navigating to the app's url, but does not await it (only preparatory async calls are awaited):
      this.appLauncher.launchAppAsync(),
      /* Just for visual effects (don't let it finish too quickly). If the custom url scheme has been
       * installed, then it's likely that the js environment is torn down before the wait completes. */
      AsyncUtils.waitAsync(1000)
    ]);
    console.log(`"${this.id}" step: bootstrap launch has been initiated!`);

    /* Wait for a while and see if Designer starts up. If it doesn't (we time out), try to detect
     * a PHC-dialog blocking the startup. */
    this.waitForBootstrapTimeoutId = setTimeout(() => {
      this.waitForBootstrapTimeoutId = null;
      /* Instead of going directly to the next step, we'll try to detect the presence of a PHC-dialog
       * and determine which event to dispatch depending on the detection outcome. */
      //this.dispatchEvent({ id: EventIds.gaveUpWaitingForBootstrapLaunch })
      this.tryDetectBrowserBlockDialog();
    }, this.config.bootstrapLaunchWaitDuration);
  }

  done() {
    this.$launchSpinner.hide();
    if (this.buttons) {
      this.popupDialog.removeButtons(this.buttons);
    }
    if (this.waitForBootstrapTimeoutId) {
      clearTimeout(this.waitForBootstrapTimeoutId);
      this.waitForBootstrapTimeoutId = null;
    }
    if (this.dialogDetectionTimeoutId) {
      clearTimeout(this.dialogDetectionTimeoutId);
      this.dialogDetectionTimeoutId = null;
    }
    super.done();
  }
}

/**
 * This step is activated when we receive notice from the Bootstrap app that it has launched the
 * Designer ClickOnce app. But, for the time being the content will not be visible because the popup
 * will be immediately dismissed (the step auto-completes because we consider the process done).
 *
 * In the future we'll have the Designer application notify us when it has loaded (after any ClickOnce
 * install/upgrade), and this content will be visible until we get that notification.
 *
 * If a PHC-Dialog was displayed by the browser (see further below for more about PCH-Dialogs),
 * then this content will be displayed after the user gets passed the PHC-Dialog. The app might
 * still not start, if it's the first time or there's a ClickOnce upgrade. In those cases the
 * ClickOnce runtime will download the app and then start it.
 */
export class AppLaunchedStep extends ProcessStep {
  constructor(appLauncher, config) {
    super(config);
    this.appLauncher = appLauncher;
    this.contentSelector = '.step-id-app-launched'; // The super.doExecute() method uses this property
  }

  async doExecute() {
    await super.doExecute();
    this.popupDialog.close();
    // Send "designer_started" event to Google Tag Manager (GTM):
    if (this.appLauncher.pushDataLayerEvent) {
      window.dataLayer.push({
        event: 'designer_started',
        app_id: this.appLauncher.appId,
        enduser_mode: this.appLauncher.isEndUser, // True if we're starting the app in end-user-mode
        actual_user_type: this.appLauncher.userType // The actual user type taken from the currently logged in user
      });
    }
  }
}

/**
 * This step is activated when we detect the presence of a PHC-dialog. This is a modal dialog
 * displayed by the browser blocking the user from interacting with the page. All we can do is
 * display enough information inside our own dialog (which might be partly blurred) to give the
 * user the confidence to accept the PHC-dialog and open our program. We will also include a
 * download button inside our dialog, but this button can only be clicked after the user dismisses
 * the PHC-dialog.
 */
export class HelpGetAppLaunchedStep extends ProcessStep {
  constructor(config) {
    super(config);
    this.contentSelector = '.step-id-help-get-app-launched'; // The super.doExecute() method uses this property
    this.buttons = [this.addDownloadButton()];
  }

  async doExecute() {
    await super.doExecute();
    this.popupDialog.addButtons(this.buttons);
    this.popupDialog.setWidth(600); // Increase the width to make the image/video more visible
    const topMargin = 225; // 225 seems to place our popup just below the PHC-dialog (2/19/2024)
    this.popupDialog.onWindowResized = mon => {
      this.popupDialog.animateDialogToVertTopAndHorizCenter(/*skipAnimation:*/ true, topMargin);
    }
    this.popupDialog.startMonitorWindowResizing();
    this.popupDialog.animateDialogToVertTopAndHorizCenter(/*skipAnimation:*/ false, topMargin);
  }

  done() {
    this.popupDialog.stopMonitorWindowResizing();
    this.popupDialog.removeButtons(this.buttons, /*keepCurrentPosition:*/ true);
    super.done();
  }
}

/**
 * This step is activated if we have not received notice from the Bootstrap app that it has launched
 * the Designer ClickOnce app, and we have not detected an open PHC-dialog. This could happen if the
 * user chose to start the app, but have not yet downloaded & installed it. The step will explain the
 * situation and show a download button for starting a new download & install.
 */
export class StartFailedTryDownloadStep extends ProcessStep {
  constructor(config) {
    super(config);
    this.contentSelector = '.step-id-help-or-download'; // The super.doExecute() method uses this property
    this.buttons = [this.addDownloadButton()];
  }

  async doExecute() {
    await super.doExecute();
    this.popupDialog.addButtons(this.buttons);
  }

  done() {
    this.popupDialog.removeButtons(this.buttons);
    super.done();
  }
}

//#endregion --------------- Starting Designer---------------


//#region --------------- Downloading/installing Designer ---------------

/** This step is responsible for kick-starting the process when the user clicks on the Download button. */
export class DownloadButtonStep extends ProcessStep {
  constructor(telemetrySession, config) {
    super(config);
    this.telemetrySession = telemetrySession;
    const downloadBtn = jQuery(config.downloadDesignerBtnSelector);
    downloadBtn.show();
    downloadBtn.click(async e => {
      e.preventDefault(); // If the target is an anchor, this prevent the default action (navigating to the href)
      await this.execute();
    });
  }

  /**
   * Executes the DownloadButtonStep, e.g. the user has initiated the download by clicking on a button.
   * @param {any} initiatingEvent The event which caused this step to be started.
   */
  async doExecute() {
    await super.doExecute();
    this.telemetrySession.reportDesignerButtonClickedAsync(); // We don't need to await this
    this.popupDialog.open();
    this.dispatchEvent({ id: EventIds.appDownloadInitiated });
  }
}

/**
 * This step is responsible for downloading the Bootstrap program. The step is active while the
 * download is going on and will continue to the next step as soon as the download completes.
 */
export class DownloadingStep extends ProcessStep {
  constructor(appLauncher) {
    super();
    this.contentSelector = '.step-id-downloading'; // The super.doExecute() method uses this property
    this.appLauncher = appLauncher;
  }

  async doExecute() {
    await super.doExecute();
    this.$downloadSpinner = this.popupDialog.findChildElement('.download-spinner');
    this.$downloadSpinner.show();
    console.log(`"${this.id}" step: starting bootstrap download...`);

    await Promise.all([
      this.appLauncher.downloadAppAsync(),
      AsyncUtils.waitAsync(2000) // Just for visual effects (don't let it finish too quickly)
    ]);

    console.log(`"${this.id}" step: bootstrap downloaded!`);
    this.$downloadSpinner.hide();
    /* We now know the bootstrap app has downloaded, but we don't know if the user has started it.
      * That's for the WaitingForInstallStep to find out. The appLauncher has started monitoring
      * bootstrap status changes, and the subsequent steps will handle those status change events. */
    this.dispatchEvent({ id: EventIds.appDownloaded });
  }

  done() {
    super.done();
    this.$downloadSpinner.hide();
  }
}

/**
 * This step is responsible for helping the user start the bootstrap install program by showing
 * where the browser places downloads, and indicating how to start the download install.
 */
export class WaitingForInstallStep extends ProcessStep {
  constructor(appLauncher, deviceTestResults) {
    super();
    this.isNewDownloadExperience = true; // Default to true
    const device = deviceTestResults.clientDevice;
    if ((deviceTestResults.isGoogleChrome || deviceTestResults.isEdge) && device.browserVersion < 91) {
      this.isNewDownloadExperience = false;
    } else if (deviceTestResults.isFirefox && device.browserVersion < 26) {
      this.isNewDownloadExperience = false;
    }
    this.contentSelector = '.step-id-wait-for-install'; // The super.doExecute() method uses this property
    if (!this.isNewDownloadExperience) {
      this.contentSelector += '-legacy';
    }
    this.appLauncher = appLauncher;
  }

  async doExecute() {
    await super.doExecute();
    /* We're not doing anything here except updating the popup's content, size, and moving
     * the popup to an appropriate position. All we need is for the super.doExecute() method to activate
     * the UI for the step. We're relying on the appLauncher's monitoring process to notify us whenever
     * the bootstrap status changes and takes us from this step to the InstallingStep or subsequent steps. */

    this.popupDialog.setWidth(600); // Increase the width to make the image/video more visible

    const moveWindow = (skipAnimation) => {
      if (this.isNewDownloadExperience) {
        this.popupDialog.animateDialogToVertTopAndHorizCenter(skipAnimation);
      } else {
        this.popupDialog.animateDialogToVertBottomAndHorizCenter(skipAnimation);
      }
    };

    this.popupDialog.onWindowResized = mon => {
      moveWindow(/*skipAnimation:*/ true);
    }
    this.popupDialog.startMonitorWindowResizing();
    moveWindow(/*skipAnimation:*/ false);
  }

  done() {
    this.popupDialog.stopMonitorWindowResizing();
    super.done();
  }
}

/**
 * This step is active while the Bootstrap program is installing (from the download folder).
 * If the install finishes quickly enough, then this step will be skipped, and the AppLaunched
 * step is activated instead (from receiving AppLaunched status change from the Bootstrap app).
 */
export class InstallingStep extends ProcessStep {
  constructor() {
    super();
    this.contentSelector = '.step-id-installing'; // The super.doExecute() method uses this property
  }
}

/**
 * This step is similar to the InstallingStep. It's activated by the Bootstrap app as soon as it's
 * done installing, but before the Designer app is launched. If the install finishes quickly enough,
 * then this step will be skipped, and the AppLaunched step is activated instead (from receiving
 * AppLaunched status change from the Bootstrap app).
 */
export class InstalledStep extends ProcessStep {
  constructor() {
    super();
    this.contentSelector = '.step-id-installed'; // The super.doExecute() method uses this property
  }
}

//#endregion --------------- Downloading/installing Designer ---------------


/** This step is responsible for handling errors occurring during the process. */
export class UnexpectedErrorStep extends ProcessStep {
  constructor(config) {
    super(config);
    this.contentSelector = '.step-id-unexpected-error';
  }

  async doExecute() {
    try {
      await super.doExecute();
      const error = this.initiatingEvent.data;
      let errorMsg;
      if (typeof error === 'string') {
        errorMsg = error;
      } else if (error instanceof Error) {
        errorMsg = error.message;
        if (error.stack) {
          errorMsg += `\nError call stack: ${error.stack}`;
        }
      } else if (error && error.message) {
        errorMsg = error.message;
      }
      this.buttons = [
        {
          key: 'copy-to-clipboard-btn',
          class: 'copy-to-clipboard',
          text: this.config.copyErrorToClipboardBtnText,
          onAddButtonStyle: {
            // See the comment in jQueryPopupDialog.applyButtonStyles method for more on why we do it this way:
            callback: (index, button, config) => {
              button.classList.add('btn-outline-secondary');
              // Inserts the clipboard svg icon from our common-icons svg sprite file:
              jQuery(button).prepend('<svg class="info-square" style="width:16px;height:16px;fill:none;stroke:currentColor;margin-right:5px;margin-bottom:3px"><use xlink:href="/svg/common-icons.svg#clipboard"></use></svg>');
              return true; // Handled -> we did the styling, don't change it!
            }
          },
          click: async (event, a, b) => {
            await navigator.clipboard.writeText(errorMsg);
            const $feedback = this.popupDialog.findChildElement(".click-feedback");
            if ($feedback) {
              $feedback.fadeIn(300).delay(1000).fadeOut(300); // Temporarily show the feedback message
            }
          }
        }
      ];
      this.popupDialog.addButtons(this.buttons);
      if (this.config.useGlobalErrorHandler) {
        await GlobalErrorHandler.HandleException(error);
      }
    } catch (e) {
      /* The wrapping execute() method has a try/catch clause, and in the catch clause this.dispatchException()
       * is called. Since we're already handling an error, letting exceptions escape this method would lead to
       * an endless recursive situation. Hence, swallow exceptions now! */
      console.error(e);
    }
  }

  done() {
    this.popupDialog.removeButtons(this.buttons);
    super.done();
  }
}

/** This step is responsible for ending the process. */
export class FinalStep extends ProcessStep {
  constructor(appLauncher) {
    super();
    this.appLauncher = appLauncher;
  }

  async doExecute() {
    await super.doExecute();
    this.appLauncher.stopMonitoringBootstrapStatus();
  }
}
